<template>
  <div @click="openDropdown = false">
    <div class="space-y-4">
      <!-- Page header -->
      <div class="flex items-center gap-3">
        <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
          <span class="material-icons-round">people_alt</span>
        </span>
        <div>
          <h2 class="text-lg font-semibold text-gray-800">Drivers</h2>
          <p class="text-sm text-gray-500">Manage drivers, their documents and status.</p>
        </div>
      </div>
      <div class="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm">
        <div class="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div class="flex items-center space-x-1 text-gray-500">
            <span class="text-base material-icons-round">filter_list</span>
            <span class="text-xs font-semibold tracking-widest uppercase">Filters</span>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <export-button
              :downloadUrl="reportDownloadUrl"
              :working="loading"
            ></export-button>
            <export-button
              :downloadUrl="driverFinanceReportDownloadUrl"
              :working="loading"
              label="driver finance"
              disabled-label="driver finance disabled"
              :disabled="!$store.getters.role.permissions['drivers-finance'] || !$store.getters.role.permissions['drivers-finance'].canAccess"
            ></export-button>
            <router-link
              to="/drivers/add"
              class="inline-flex items-center h-10 px-4 text-sm font-semibold text-white rounded-xl shadow-sm bg-taxiye-500 transition-colors duration-150 hover:bg-taxiye-600"
            >
              <span class="mr-1 text-base material-icons-round">add</span>
              Add Driver
            </router-link>
          </div>
        </div>

        <div class="flex flex-wrap items-end gap-4">
          <!-- Search -->
          <div class="relative w-full sm:w-64">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Search Name</label>
            <div class="relative mt-1.5">
              <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span class="text-base text-gray-500 material-icons-round">search</span>
              </span>
              <input
                v-model="searchText"
                class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Search drivers by name..."
              />
            </div>
          </div>

          <!-- Status -->
          <div class="w-full sm:w-40">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Status</label>
            <select
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
              @change="setApprovedFilter"
            >
              <option :selected="selectedApprovedFilter === 'both'" value="both">All Status</option>
              <option :selected="selectedApprovedFilter === 'approved'" value="approved">Approved</option>
              <option :selected="selectedApprovedFilter === 'unapproved'" value="unapproved">Unapproved</option>
            </select>
          </div>

          <!-- Employment -->
          <div class="w-full sm:w-40">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Employment</label>
            <select
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
              @change="setEmployeementFilter"
            >
              <option :selected="selectedEmployeementFilter === 'all'" value="all">All</option>
              <option :selected="selectedEmployeementFilter === 'employee'" value="employee">Employee</option>
              <option :selected="selectedEmployeementFilter === 'not-employee'" value="not-employee">Not Employee</option>
            </select>
          </div>

          <!-- Completeness -->
          <div class="w-full sm:w-40">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Completeness</label>
            <select
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
              v-model="completed"
              @change="fetchDrivers"
            >
              <option value="all">All</option>
              <option value="true">Complete</option>
              <option value="false">Incomplete</option>
            </select>
          </div>

          <!-- Date range -->
          <div class="w-full sm:w-auto">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Date Range</label>
            <div class="flex items-center h-10 mt-1.5 bg-white border border-gray-300 shadow-sm rounded-xl">
              <span class="pl-3 pr-1 text-base text-gray-500 material-icons-round">event</span>
              <v-date-picker v-model="range" is-range timezone="UTC">
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="flex items-center">
                    <input
                      :value="inputValue.start"
                      v-on="inputEvents.start"
                      placeholder="From"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                    <span class="mx-1 text-base text-gray-300 material-icons-round">arrow_forward</span>
                    <input
                      :value="inputValue.end"
                      v-on="inputEvents.end"
                      placeholder="To"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                  </div>
                </template>
              </v-date-picker>
              <button
                v-if="range.start || range.end"
                @click="range = { start: null, end: null }"
                type="button"
                title="Clear dates"
                class="flex items-center justify-center w-8 h-8 mx-1 text-gray-400 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none"
              >
                <span class="text-lg material-icons-round">close</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-4 overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm">
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="bg-gray-100 border-b border-gray-200">
                <th
                  v-for="col in [
                    'Name',
                    'ID',
                    'Employee',
                    'Contact Number',
                    'Rating',
                    'Balance',
                    'Vehicle Type',
                    'Status',
                    'Complete',
                    'Plate Number',
                    'Registered',
                    'Paid',
                    '',
                  ]"
                  :key="`col-${col}`"
                  class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase whitespace-no-wrap"
                >
                  {{ col }}
                </th>
              </tr>
            </thead>
            <tbody class="relative" v-if="drivers.length">
              <tr
                v-for="(driver, index) in drivers"
                :key="driver._id"
                class="transition-colors duration-150 hover:bg-taxiye-50"
                :class="
                  driver.isEmployee
                    ? 'bg-yellow-50'
                    : index % 2 === 0
                    ? 'bg-white'
                    : 'bg-gray-100'
                "
              >
                <!-- Name -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <router-link :to="`/drivers/${driver._id}`" class="flex items-center space-x-3 group">
                    <img
                      v-if="driver.profileImage"
                      class="object-cover flex-shrink-0 w-10 h-10 rounded-full"
                      :src="driver.profileImage"
                      :alt="personLabel(driver)"
                    />
                    <span
                      v-else
                      class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-xs font-bold rounded-full"
                      :class="avatarClass(personLabel(driver))"
                    >{{ initials(personLabel(driver)) }}</span>
                    <span class="font-medium text-gray-900 group-hover:text-taxiye-600">
                      {{ personLabel(driver, "Unknown") }}
                    </span>
                  </router-link>
                </td>
                <!-- ID -->
                <td class="px-5 py-4 text-gray-500 whitespace-no-wrap">
                  {{ driver.driverId != undefined ? driver.driverId : "—" }}
                </td>
                <!-- Employee -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span
                    class="inline-flex px-2 py-1 text-xs font-medium rounded-md"
                    :class="driver.isEmployee ? 'text-yellow-700 bg-yellow-100' : 'text-gray-600 bg-gray-100'"
                  >
                    {{ driver.isEmployee ? "Employee" : "No" }}
                  </span>
                </td>
                <!-- Contact number -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  {{ driver.phoneNumber }}
                </td>
                <!-- Rating -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <div class="flex items-center space-x-1">
                    <i class="text-base text-yellow-400 material-icons-round">star</i>
                    <span class="font-medium text-gray-700">
                      {{ driver.rating.toFixed(1) }}
                    </span>
                  </div>
                </td>
                <!-- Balance -->
                <td class="px-5 py-4 font-medium text-gray-900 whitespace-no-wrap">
                  {{ driver.ballance.toFixed(2) }}
                </td>
                <!-- Vehicle type -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  {{
                    driver.vehicle && driver.vehicle.vehicleType
                      ? driver.vehicle.vehicleType.name
                      : "—"
                  }}
                </td>
                <!-- Status -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span
                    class="inline-flex items-center px-2 py-1 text-xs font-semibold leading-none rounded-full"
                    :class="driver.approved ? 'text-green-700 bg-green-100' : 'text-red-700 bg-red-100'"
                  >
                    <span
                      class="w-2 h-2 mr-2 rounded-full"
                      :class="driver.approved ? 'bg-green-500' : 'bg-red-500'"
                    ></span>
                    {{ driver.approved ? "Approved" : "Not Approved" }}
                  </span>
                </td>
                <!-- Complete -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <i
                    class="text-xl material-icons-round"
                    :class="driver.completed ? 'text-green-500' : 'text-gray-300'"
                    :title="driver.completed ? 'Complete' : 'Incomplete'"
                  >{{ driver.completed ? "check_circle" : "cancel" }}</i>
                </td>
                <!-- Plate number -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span
                    v-if="driver.vehicle"
                    class="inline-flex px-2 py-1 font-mono text-xs font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded-md"
                  >{{ driver.vehicle.plateNumber }}</span>
                  <span v-else class="text-gray-400">—</span>
                </td>
                <!-- Registered -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  <div class="flex items-center space-x-2">
                    <i class="text-sm text-gray-400 material-icons-round">schedule</i>
                    <span>{{ driver.createdAt.slice(0, 10).split("-").reverse().join("-") }}</span>
                  </div>
                </td>
                <!-- Paid -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <i
                    class="text-xl material-icons-round"
                    :class="driver.hasCompletedPayment ? 'text-green-500' : 'text-gray-300'"
                    :title="driver.hasCompletedPayment ? 'Paid' : 'Not paid'"
                  >{{ driver.hasCompletedPayment ? "check_circle" : "cancel" }}</i>
                </td>
                <!-- Actions -->
                <td class="px-5 py-4 text-right whitespace-no-wrap">
                  <div
                    @click.stop
                    class="relative inline-block text-left"
                    v-click-outside="hideDropdown"
                  >
                    <button
                      @click="toggleDropdown(index)"
                      type="button"
                      class="inline-flex items-center justify-center w-9 h-9 text-gray-500 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none"
                      aria-haspopup="true"
                      :aria-expanded="openDropdown === index"
                      :ref="
                        openDropdown === index
                          ? 'drivers-dropdown-trigger-' + ref
                          : ''
                      "
                    >
                      <i class="material-icons-round">more_vert</i>
                    </button>
                    <transition
                      enter-active-class="transition duration-100 ease-out"
                      enter-class="transform scale-95 opacity-0"
                      enter-to-class="transform scale-100 opacity-100"
                      leave-active-class="transition duration-75 ease-in"
                      leave-class="transform scale-100 opacity-100"
                      leave-to-class="transform scale-95 opacity-0"
                    >
                      <div
                        v-if="openDropdown === index"
                        class="absolute right-0 z-10 w-56 mt-1 origin-top-right bg-white border border-gray-100 shadow-lg rounded-xl"
                        :ref="
                          openDropdown === index ? 'drivers-dropdown-' + ref : ''
                        "
                      >
                        <div
                          role="menu"
                          aria-orientation="vertical"
                          aria-labelledby="options-menu"
                          class="py-1"
                        >
                          <router-link
                            :to="`rides?driver=${driver._id}`"
                            class="flex items-center px-4 py-2 space-x-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100"
                            role="menuitem"
                            v-show="driver.vehicle"
                          >
                            <i class="text-base material-icons-round">history</i>
                            <span>Trip History</span>
                          </router-link>
                          <router-link
                            :to="`/drivers/availability/${driver._id}`"
                            class="flex items-center px-4 py-2 space-x-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100"
                            role="menuitem"
                            v-show="driver.vehicle"
                          >
                            <i class="text-base material-icons-round">event_available</i>
                            <span>Availability History</span>
                          </router-link>
                          <router-link
                            v-if="driver.vehicle && driver.vehicle.position"
                            :to="`/birds-eye-view?driver=${driver._id}&lat=${driver.vehicle.position.coordinates[1]}&long=${driver.vehicle.position.coordinates[0]}`"
                            class="flex items-center px-4 py-2 space-x-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100"
                            role="menuitem"
                          >
                            <i class="text-base material-icons-round">place</i>
                            <span>Locate Vehicle</span>
                          </router-link>
                          <router-link
                            :to="`vehicles/add?driver=${driver._id}`"
                            class="flex items-center px-4 py-2 space-x-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100"
                            role="menuitem"
                            v-show="!driver.vehicle"
                          >
                            <i class="text-base material-icons-round">directions_car</i>
                            <span>Add a Vehicle</span>
                          </router-link>
                          <router-link
                            :to="`wallet-managment?driver=${driver._id}`"
                            class="flex items-center px-4 py-2 space-x-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100"
                            role="menuitem"
                            v-show="driver.vehicle"
                          >
                            <i class="text-base material-icons-round">account_balance_wallet</i>
                            <span>Topup</span>
                          </router-link>
                          <router-link
                            :to="`rewards-wallet-managment?driver=${driver._id}`"
                            class="flex items-center px-4 py-2 space-x-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100"
                            role="menuitem"
                            v-show="driver.vehicle"
                          >
                            <i class="text-base material-icons-round">card_giftcard</i>
                            <span>Rewards Wallet</span>
                          </router-link>
                        </div>
                        <div class="py-1 border-t border-gray-100">
                          <router-link
                            :to="`drivers/${driver._id}/edit`"
                            class="flex items-center px-4 py-2 space-x-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100"
                            role="menuitem"
                          >
                            <i class="text-base material-icons-round">edit</i>
                            <span>Edit</span>
                          </router-link>
                          <button
                            @click="toggleApproval(driver._id)"
                            v-show="driver.vehicle"
                            type="button"
                            class="flex items-center w-full px-4 py-2 space-x-2 text-sm text-left text-gray-700 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100"
                            role="menuitem"
                          >
                            <i class="text-base material-icons-round">{{ driver.approved ? "close" : "check" }}</i>
                            <span>{{ driver.approved ? "Disapprove" : "Approve" }}</span>
                          </button>
                        </div>
                        <div
                          class="py-1 border-t border-gray-100"
                          v-if="
                            ($store.getters.role.permissions['device-bans'] &&
                              $store.getters.role.permissions['device-bans'].canAccess) ||
                            ($store.getters.role.permissions['banned-drivers'] &&
                              $store.getters.role.permissions['banned-drivers'].canAccess)
                          "
                        >
                          <button
                            v-if="$store.getters.role.permissions['device-bans'] && $store.getters.role.permissions['device-bans'].canAccess"
                            class="flex items-center w-full px-4 py-2 space-x-2 text-sm text-left text-red-600 transition-colors duration-150 hover:bg-red-50 focus:outline-none"
                            role="menuitem"
                            @click="showSpecificBanModal(driver.deviceID, driver._id)"
                          >
                            <i class="text-base material-icons-round">block</i>
                            <span>Ban Device (Specific)</span>
                          </button>
                          <button
                            v-if="$store.getters.role.permissions['device-bans'] && $store.getters.role.permissions['device-bans'].canAccess"
                            class="flex items-center w-full px-4 py-2 space-x-2 text-sm text-left text-red-600 transition-colors duration-150 hover:bg-red-50 focus:outline-none"
                            role="menuitem"
                            @click="showModelBanModal(driver.deviceModelID)"
                          >
                            <i class="text-base material-icons-round">block</i>
                            <span>Ban This Device Model</span>
                          </button>
                          <button
                            v-if="$store.getters.role.permissions['banned-drivers'] && $store.getters.role.permissions['banned-drivers'].canAccess"
                            class="flex items-center w-full px-4 py-2 space-x-2 text-sm text-left text-red-600 transition-colors duration-150 hover:bg-red-50 focus:outline-none"
                            role="menuitem"
                            @click="showDriverBanModal(driver._id)"
                          >
                            <i class="text-base material-icons-round">block</i>
                            <span>Ban Driver</span>
                          </button>
                        </div>
                      </div>
                    </transition>
                  </div>
                </td>
              </tr>
              <div
                v-if="loading"
                class="absolute inset-0 flex items-center justify-center w-full h-full bg-white bg-opacity-50"
              >
                <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                <span class="invisible block">Loading...</span>
              </div>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="13" class="py-16 text-center bg-white">
                  <div v-if="loading" class="inline-block">
                    <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                    <span class="invisible block">Loading...</span>
                  </div>
                  <div v-else class="flex flex-col items-center text-gray-400">
                    <span class="flex items-center justify-center w-16 h-16 mb-3 rounded-full bg-gray-100">
                      <i class="text-3xl material-icons-round">inbox</i>
                    </span>
                    <span class="text-sm font-medium text-gray-500">No drivers found</span>
                    <span class="mt-1 text-xs text-gray-400">Try adjusting your filters</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex items-end justify-between" v-if="drivers.length">
        <pagination
          class="flex space-x-2"
          :currentPage="currentPage"
          :total="total"
          @goto="goToPage"
          @setPerPage="setPerPage"
          :perPage="perPage"
          :start="start"
          :end="end"
          :count="paginator.count"
        ></pagination>
      </div>
    </div>

    <div
      v-if="showingDriverBanModal"
      class="absolute inset-0 z-40 flex items-center justify-center bg-black bg-opacity-25"
    >
      <div @click.stop class="w-full max-w-lg overflow-hidden bg-white shadow-lg rounded-2xl">
        <div class="bg-gray-100">
          <h3 class="px-6 py-4 font-semibold text-gray-600">Ban Driver</h3>
        </div>
        <div class="flex flex-col items-start px-6 py-8">
          <p class="font-medium text-gray-700">Note:</p>
          <p>
            <em class="text-sm text-gray-600">note will be displayed next time driver uses the app</em>
          </p>
          <textarea
            v-model="driverBanNote"
            class="w-full min-w-full p-2 my-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-taxiye-300"
          ></textarea>
        </div>

        <hr />
        <div class="flex items-end justify-between px-6 py-4">
          <button
            @click="showingDriverBanModal = false"
            type="button"
            class="px-4 py-2 text-gray-600 rounded-xl transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900"
          >Cancel</button>
          <button
            @click="banDriver"
            type="button"
            :disabled="loading"
            class="flex items-center px-4 py-2 space-x-2 font-semibold border border-transparent shadow-sm rounded-xl"
            :class="
              loading
                ? 'bg-red-100 text-red-400 cursor-not-allowed'
                : 'bg-red-500 text-white hover:bg-red-600'
            "
          >
            <i v-if="loading" class="material-icons-round spin">sync</i>
            <i v-else class="material-icons-round">block</i>
            <span>Ban</span>
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showBanModal"
      class="absolute inset-0 z-40 flex items-center justify-center bg-black bg-opacity-25"
    >
      <div @click.stop class="w-full max-w-lg overflow-hidden bg-white shadow-lg rounded-2xl">
        <div class="bg-gray-100">
          <h3 class="px-6 py-4 font-semibold text-gray-600">Ban Driver's Device Model</h3>
        </div>
        <div class="flex flex-col items-start px-6 py-8">
          <p class="font-medium text-gray-700">Note:</p>
          <p>
            <em class="text-sm text-gray-600">note will be displayed to all drivers with this device model</em>
          </p>
          <textarea
            v-model="banNote"
            class="w-full min-w-full p-2 my-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-taxiye-300"
          ></textarea>
        </div>

        <hr />
        <div class="flex items-end justify-between px-6 py-4">
          <button
            @click="showBanModal = false"
            type="button"
            class="px-4 py-2 text-gray-600 rounded-xl transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900"
          >Cancel</button>
          <button
            @click="banModel"
            type="button"
            :disabled="loading"
            class="flex items-center px-4 py-2 space-x-2 font-semibold border border-transparent shadow-sm rounded-xl"
            :class="
              loading
                ? 'bg-red-100 text-red-400 cursor-not-allowed'
                : 'bg-red-500 text-white hover:bg-red-600'
            "
          >
            <i v-if="loading" class="material-icons-round spin">sync</i>
            <i v-else class="material-icons-round">block</i>
            <span>Ban</span>
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showBanModalSpecific"
      class="absolute inset-0 z-40 flex items-center justify-center bg-black bg-opacity-25"
    >
      <div @click.stop class="w-full max-w-lg overflow-hidden bg-white shadow-lg rounded-2xl">
        <div class="bg-gray-100">
          <h3 class="px-6 py-4 font-semibold text-gray-600">Ban Driver's Device</h3>
        </div>
        <div class="flex flex-col items-start px-6 py-8">
          <p class="font-medium text-gray-700">Note:</p>
          <p>
            <em class="text-sm text-gray-600">Note will be displayed to the particular driver's device</em>
          </p>
          <textarea
            v-model="banNoteSpecific"
            class="w-full min-w-full p-2 my-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-taxiye-300"
          ></textarea>
        </div>

        <hr />
        <div class="flex items-end justify-between px-6 py-4">
          <button
            @click="showBanModalSpecific = false"
            type="button"
            class="px-4 py-2 text-gray-600 rounded-xl transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900"
          >Cancel</button>
          <button
            @click="banDevice"
            type="button"
            :disabled="loading"
            class="flex items-center px-4 py-2 space-x-2 font-semibold border border-transparent shadow-sm rounded-xl"
            :class="
              loading
                ? 'bg-red-100 text-red-400 cursor-not-allowed'
                : 'bg-red-500 text-white hover:bg-red-600'
            "
          >
            <i v-if="loading" class="material-icons-round spin">sync</i>
            <i v-else class="material-icons-round">block</i>
            <span>Ban</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import PaginationMixin from "@/mixins/Pagination";
import ExportButton from "@/components/ExportButton";
import Pagination from "@/components/Pagination";

export default {
  mixins: [PaginationMixin],
  components: {
    ExportButton,
    Pagination,
  },
  data() {
    return {
      drivers: [],
      openDropdown: false,
      loading: true,
      selectedApprovedFilter: "",
      selectedEmployeementFilter: "",
      searchText: "",
      range: {
        start: null,
        end: null,
      },
      ref: "",
      completed: "all",
      showBanModalSpecific: false,
      showBanModal: false,
      showingDriverBanModal: false,

      banNoteSpecific: "",
      banNote: "",
      deviceId: null,
      driverId: null,
      deviceModelId: null,

      driverToBan: null,
      driverBanNote: null,
    };
  },
  created() {
    this.fetchDrivers();
  },
  mounted() {
    this.ref = Math.random().toString(36).substr(2, 5);
  },
  beforeDestroy() {
    document.body.removeEventListener("click", this.clickAwayHandler);
  },
  computed: {
    // to trigger fetchDrivers
    dataRefreshTrigger() {
      return [
        this.selectedApprovedFilter,
        this.selectedEmployeementFilter,
        this.currentPage,
        this.range.start,
        this.range.end,
        this.searchText,
      ].join("|");
    },
    reportDownloadUrl() {
      let filter;

      if (this.selectedApprovedFilter === "approved") {
        filter = true;
      } else if (this.selectedApprovedFilter === "unapproved") {
        filter = false;
      } else {
        filter = null;
      }

      return (
        "drivers/export?" +
        Object.entries({
          q: this.searchText,
          ...(this.range || {}),
          approved: filter,
        })
          .map(([key, value]) => [key, value].join("="))
          .join("&")
      );
    },
    driverFinanceReportDownloadUrl() {
      return (
        "drivers-finance/export?" +
        Object.entries({
          ...Object.fromEntries(
            Object.entries({
              ...(this.range ? { from: this.range.start, to: this.range.end } : {} || {}),
            }).filter(([key, val]) => key && val)
          ),
        })
          .map(([key, value]) => [key, value].join("="))
          .join("&")
      );
    },
  },
  watch: {
    dataRefreshTrigger() {
      this.fetchDrivers();
    },
    perPage() {
      this.currentPage === 1 ? this.fetchDrivers() : (this.currentPage = 1);
    },
    openDropdown(newVal) {
      if (newVal !== false) {
        document.body.addEventListener("click", this.clickAwayHandler);
      } else {
        document.body.removeEventListener("click", this.clickAwayHandler);
      }
    },
    searchText() {
      this.currentPage = 1;
    },
  },
  methods: {
    setApprovedFilter(e) {
      this.selectedApprovedFilter = e.target.value;
    },
    setEmployeementFilter(e) {
      this.selectedEmployeementFilter = e.target.value;
    },
    personLabel(person, fallback = "") {
      if (!person) return fallback;
      const name = `${person.firstName || ""} ${person.lastName || ""}`.trim();
      if (!name) return fallback;
      return name
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    },
    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    avatarClass(name) {
      const palette = [
        "bg-taxiye-100 text-taxiye-600",
        "bg-indigo-50 text-indigo-600",
        "bg-blue-50 text-blue-600",
        "bg-green-50 text-green-600",
        "bg-purple-50 text-purple-600",
        "bg-pink-50 text-pink-600",
      ];
      let hash = 0;
      for (let i = 0; i < (name || "").length; i++) {
        hash = (hash + name.charCodeAt(i)) % palette.length;
      }
      return palette[hash];
    },
    showSpecificBanModal(deviceId, driverId) {
      this.openDropdown = false;
      if (!deviceId) {
        return window.flash({
          type: "error",
          body: "No Device ID found for the driver!",
        });
      } else {
        this.showBanModal = false;
        this.showBanModalSpecific = true;
        this.showingDriverBanModal = false;

        this.banNote = "";
        this.banNoteSpecific = "";
        this.driverBanNote = "";

        this.deviceId = deviceId;
        this.driverId = driverId;
      }
    },
    showModelBanModal(deviceModelId) {
      this.openDropdown = false;
      if (!deviceModelId) {
        return window.flash({
          type: "error",
          body: "No Device Model found for the driver!",
        });
      } else {
        this.showBanModal = true;
        this.showBanModalSpecific = false;
        this.showingDriverBanModal = false;

        this.banNote = "";
        this.banNoteSpecific = "";
        this.driverBanNote = "";

        this.deviceModelId = deviceModelId;
      }
    },
    showDriverBanModal(driverId) {
      this.openDropdown = false;

      this.driverToBan = driverId;

      this.showBanModal = false;
      this.showBanModalSpecific = false;
      this.showingDriverBanModal = true;
      this.banNote = "";
      this.banNoteSpecific = "";

      this.driverBanNote = "";
    },
    toggleDropdown(index) {
      if (this.openDropdown === index) {
        this.openDropdown = false;
      } else {
        this.openDropdown = index;
      }
    },
    hideDropdown() {
      this.openDropdown = false;
    },
    fetchDrivers: _.debounce(function () {
      this.loading = true;

      let filter;

      if (this.selectedApprovedFilter === "approved") {
        filter = true;
      } else if (this.selectedApprovedFilter === "unapproved") {
        filter = false;
      } else {
        filter = null;
      }

      let employeesFilter;

      if (this.selectedEmployeementFilter === "employee") {
        employeesFilter = true;
      } else if (this.selectedEmployeementFilter === "not-employee") {
        employeesFilter = false;
      } else {
        employeesFilter = null;
      }

      this.$misc
        .get(`/drivers/admin-search`, {
          params: {
            q: this.searchText,
            from: this.range.start ? this.range.start : undefined,
            to: this.range.end ? this.range.end : undefined,
            approved: filter,
            employeesOnly: employeesFilter,
            completed:
              this.completed && ["true", "false"].includes(this.completed)
                ? this.completed
                : "all",
            page: this.currentPage,
            limit: this.perPage,
          },
        })
        .then((response) => {
          this.drivers = response.data.data;
          this.paginator = {
            nextPage: response.data.nextPage,
            prevPage: response.data.prevPage,
            count: response.data.count,
          };
        })
        .catch((err) => console.log(err))
        .finally(() => setTimeout(() => (this.loading = false), 300));
    }, 500),
    toggleApproval(id) {
      const currentStatus = this.drivers.find((driver) => driver._id === id)
        .approved;
      this.$misc
        .patch(`/drivers/${id}`, {
          approved: !currentStatus,
        })
        .then(() => {
          window.flash({
            type: "success",
            body: currentStatus ? "Driver disapproved!" : "Driver approved!",
          });
          this.fetchDrivers();
        })
        .catch((err) => {
          window.flash({ type: "error", body: "Failed to approve driver!" });
          console.log(err);
        })
        .finally(() => (this.openDropdown = false));
    },
    clickAwayHandler(e) {
      if (
        (this.$refs["drivers-dropdown-trigger-" + this.ref] &&
          this.$refs["drivers-dropdown-trigger-" + this.ref][0].contains(
            e.target
          )) ||
        this.$refs["drivers-dropdown-" + this.ref] === e.target
      ) {
        return;
      }

      this.openDropdown = false;
    },
    banDriver() {
      this.$misc
        .post(`/driver-bans/ban-driver`, {
          driver: this.driverToBan,
          note: this.driverBanNote,
        })
        .then(() => {
          window.flash({
            type: "success",
            body: "Device Banned successfully!",
          });
          this.banNote = "";
          this.banNoteSpecific = "";
          this.driverBanNote = "";
          this.deviceId = null;
          this.driverId = null;
          this.fetchDrivers();
        }).catch(err => {
          window.flash({
            type: "error",
            body: err.response && err.response.data,
          });
        }).finally(() => {
          this.showBanModal = false;
          this.showBanModalSpecific = false;
          this.showingDriverBanModal = false;
        });
    },
    banDevice() {
      this.$misc
        .post(`/bans/ban-device`, {
          deviceID: this.deviceId,
          driverID: this.driverId,
          note: this.banNoteSpecific,
        })
        .then(() => {
          window.flash({
            type: "success",
            body: "Device Banned successfully!",
          });
          this.banNote = "";
          this.banNoteSpecific = "";
          this.deviceId = null;
          this.driverId = null;
        }).catch(err => {
          window.flash({
            type: "error",
            body: err.response && err.response.data,
          });
        }).finally(() => {
          this.showBanModal = false;
          this.showBanModalSpecific = false;
          this.showingDriverBanModal = false;
        });
    },
    banModel() {
      this.$misc
        .post(`/bans/ban-model`, {
          deviceModelID: this.deviceModelId,
          note: this.banNote,
        })
        .then(() => {
          window.flash({
            type: "success",
            body: "Device Model Banned successfully!",
          });
          this.banNote = "";
          this.banNoteSpecific = "";
          this.driverBanNote = "";

          this.deviceId = null;
          this.driverId = null;
        }).finally(() => {
          this.showBanModal = false;
          this.showBanModalSpecific = false;
          this.showingDriverBanModal = false;
        });
    },
  },
};
</script>
